import { useState, useCallback, useRef, useMemo, memo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Divider,
  Flex,
  Icon,
  List,
  ListItem,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  useDisclosure,
} from "@chakra-ui/react";
import { FaChevronDown, FaPlus } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import SocialAccountPropType from "../../../_types/SocialAccount";
import SearchInput from "../SearchInput/SearchInput";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import AnalyticsStatusIcon from "../../../_containers/Analytics/AnalyticsStatusIcon";
import { ANALYTICS_STATUSES } from "../../../_constants/AnalyticsStatuses";

const SocialAccountsDropdown = ({
  accounts,
  dropdownBackground,
  isDisabled,
  onAddAccount,
  onChange,
  selected,
  showAnalyticsStatus,
  width,
  ...rest
}) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { t } = useTranslation("planner");
  const [filterAccountsQuery, setFilterAccountsQuery] = useState("");
  const searchInputRef = useRef(null);

  const handleSelect = useCallback(
    (account) => {
      onChange(account);
      onClose();
    },
    [onChange, onClose]
  );

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === selected),
    [selected, accounts]
  );

  const onFilterAccountsQueryChange = useCallback(
    (value) => {
      if (value.length >= 1) {
        setFilterAccountsQuery(value.toLowerCase());
      } else {
        setFilterAccountsQuery("");
      }
    },
    [setFilterAccountsQuery]
  );

  const filterAccounts = useCallback(
    (account) => {
      return (
        !filterAccountsQuery ||
        account?.name?.toLowerCase()?.indexOf(filterAccountsQuery) > -1
      );
    },
    [filterAccountsQuery]
  );

  const filteredAccounts = useMemo(() => {
    if (accounts?.length) {
      return accounts.filter(filterAccounts);
    }

    return [];
  }, [accounts, filterAccounts]);

  if (!selectedAccount) {
    return null;
  }

  return (
    <Popover
      onOpen={onOpen}
      onClose={onClose}
      isOpen={isOpen}
      isLazy
      initialFocusRef={searchInputRef}
      placement="bottom-end"
      w={width}
    >
      <PopoverTrigger>
        <Button
          variant="ghost"
          leftIcon={
            <SocialAccountAvatar
              name={selectedAccount.name}
              src={selectedAccount.image}
              socialType={selectedAccount.social_type}
              socialTypeShown
              size="sm"
              mr="1"
            />
          }
          rightIcon={<Icon mr="3" fontSize="sm" as={FaChevronDown} />}
          aria-label="Selected account"
          h="auto"
          w={width}
          p="2"
          textAlign="left"
          bg="gray.900"
          isDisabled={isDisabled}
          {...rest}
        >
          <Flex justify="center" direction="column" overflow="hidden" flex="1">
            <Text
              isTruncated
              as="span"
              d="block"
              fontSize="sm"
              lineHeight="1em"
              mb="1"
              fontWeight="normal"
            >
              {selectedAccount.name}
            </Text>
            <Text
              isTruncated
              as="span"
              fontSize="xs"
              color="gray.200"
              lineHeight="1em"
              fontWeight="normal"
            >
              {t(`social_types.${selectedAccount.social_type}.full`)}
              {selectedAccount.parent
                ? ` (${selectedAccount.parent.name})`
                : ""}
            </Text>
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent w="240px" background={dropdownBackground}>
        <PopoverHeader p="2">
          <SearchInput
            size="sm"
            placeholder={t("accounts.search")}
            onChange={onFilterAccountsQueryChange}
            onClose={
              filterAccountsQuery?.length
                ? () => onFilterAccountsQueryChange("")
                : undefined
            }
            value={filterAccountsQuery}
            ref={searchInputRef}
          />
        </PopoverHeader>
        <PopoverBody p="0">
          <List fontSize="sm" maxHeight="300px" overflow="auto">
            {onAddAccount && (  
              <ListItem>
                <Button
                  bg="none"
                  w="100%"
                  borderRadius="0"
                  pl="0.7rem"
                  pt="1"
                  pb="1"
                  justifyContent="flex-start"
                  onClick={onAddAccount}
                  leftIcon={<Icon bg="whiteAlpha.100" p="2" as={FaPlus} mr="0.1rem" ml="0" borderRadius="full" w="7" h="7" />}
                  textAlign="left"
                  fontSize="sm"
                >
                  {t(`accounts.add`)}
                </Button>
                <Divider borderColor="gray.400" opacity="1" />
              </ListItem>
            )}
            {filteredAccounts.map((account) => (
              <ListItem my="1" key={account.id}>
                <Button
                  bg="none"
                  p="2"
                  w="100%"
                  borderRadius="0"
                  justifyContent="flex-start"
                  key={account.id}
                  onClick={() => handleSelect(account)}
                  leftIcon={
                    <SocialAccountAvatar
                      name={account.name}
                      src={account.image}
                      socialType={account.social_type}
                      socialTypeShown
                      size="sm"
                    />
                  }
                  textAlign="left"
                  opacity={account.analyticsStatus === ANALYTICS_STATUSES.not_supported ? 0.5 : 1}
                >
                  <Flex
                    justifyContent="center"
                    direction="column"
                    fontWeight="normal"
                    overflow="hidden"
                    flex="1"
                  >
                    <Text
                      as="span"
                      isTruncated
                      display="block"
                      fontSize="sm"
                      lineHeight="1em"
                      mb="1"
                    >
                      {account.name}
                    </Text>
                    <Text
                      as="span"
                      isTruncated
                      maxWidth="100%"
                      display="block"
                      fontSize="xs"
                      fontWeight="normal"
                      color="gray.200"
                      lineHeight="1em"
                    >
                      {t(`social_types.${account.social_type}.full`)}
                      {account.parent ? ` (${account.parent.name})` : ""}
                    </Text>
                  </Flex>
                  {showAnalyticsStatus && (<AnalyticsStatusIcon status={account.analyticsStatus} />)}
                </Button>
              </ListItem>
            ))}
          </List>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

SocialAccountsDropdown.propTypes = {
  accounts: PropTypes.arrayOf(SocialAccountPropType).isRequired,
  dropdownBackground: PropTypes.string,
  isDisabled: PropTypes.bool,
  onAddAccount: PropTypes.func,
  onChange: PropTypes.func,
  selected: PropTypes.string,
  showAnalyticsStatus: PropTypes.bool,
  width: PropTypes.string,
};

SocialAccountsDropdown.defaultProps = {
  dropdownBackground: undefined,
  isDisabled: undefined,
  onAddAccount: undefined,
  onChange: undefined,
  selected: undefined,
  showAnalyticsStatus: false,
  width: "240px",
};

export default memo(SocialAccountsDropdown);
